import { useMutation } from '@tanstack/react-query';
import { useToast } from '@pw-tech/omni-ui';
import { API_CACHE_KEYS } from '@/modules/common/constants';
import useIOIFileUpload from './useIOIFileUpload';

const uploadToS3 = async (uploadUrl: string, fileUploaded: File) => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: fileUploaded,
    });
    return response;
  } catch (error) {
    console.warn('S3 upload error:', error);
    throw error;
  }
};

const useUploadFile = () => {
  const { toast: showToaster } = useToast();
  const { mutateAsync: ioiMutateAsync } = useIOIFileUpload();

  const { isLoading, mutate, mutateAsync } = useMutation(
    [API_CACHE_KEYS.UPLOAD_FILE],
    async ({
      file,
      onUploadProgress,
    }: {
      file: File;
      onUploadProgress: (progress: number) => void;
    }) => {
      // Step 1: Get signed URL
      onUploadProgress(40);

      const result: any = await ioiMutateAsync({ file, onUploadProgress });

      if (!result?.data?.success) {
        onUploadProgress(0);
        throw new Error('Failed to get signed URL');
      }

      // Step 2: Upload to S3
      onUploadProgress(60);
      const signedResponse = await uploadToS3(result?.data?.data?.uploadUrl, file);

      if (!signedResponse?.ok) {
        onUploadProgress(0);
        throw new Error('S3 upload failed');
      }

      onUploadProgress(80);
      // Step 3: Return final response
      const finalResponse = {
        data: {
          data: {
            ...result?.data?.data,
            fullUrl: `${result?.data?.data?.baseUrl}${result?.data?.data?.key}`,
          },
        },
      };

      return finalResponse;
    },
    {
      onError: (error: any) => {
        showToaster({
          message: error?.message ?? 'File Upload Error',
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      },
    }
  );

  return {
    isLoading,
    mutateAsync,
    mutate,
  };
};

export default useUploadFile;
